import { Controller, type FieldValues } from "react-hook-form";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import type { FormSelectProps } from "./formSelect.types";
import {
	FORM_FIELD_BASE_SX,
	FORM_FIELD_EXTRA_SMALL_SX,
	FORM_SELECT_EXTRA_SMALL_SX,
	getMuiFormFieldSize,
} from "../formFieldSize";
import { withRequiredAsterisk } from "../utils";

const DEFAULT_ACTIVE_SELECTION_COLOR = "var(--mui-palette-warning-main)";

const criarSxSelecaoAtiva = (color: string): SxProps<Theme> => ({
	"& .MuiFormLabel-root": {
		color: `${color} !important`,
	},
	"& .MuiInputLabel-root": {
		color: `${color} !important`,
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: `${color} !important`,
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
});

function isPrimitiveValue(value: unknown): value is string | number | boolean {
	return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function isSameEmptyValue(value: unknown, emptyValue?: unknown): boolean {
	if (emptyValue === undefined) return false;
	if (Object.is(value, emptyValue)) return true;
	if (value instanceof Date && emptyValue instanceof Date) return value.getTime() === emptyValue.getTime();
	if (isPrimitiveValue(value) && isPrimitiveValue(emptyValue)) return String(value) === String(emptyValue);
	if (typeof value === "object" && typeof emptyValue === "object")
		return JSON.stringify(value) === JSON.stringify(emptyValue);

	return false;
}

function possuiValorAtivo(value: unknown, emptyValue?: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (Array.isArray(value)) return value.length > 0;
	if (isSameEmptyValue(value, emptyValue)) return false;
	if (typeof value === "string") return value.trim().length > 0;
	if (typeof value === "number") return Number.isFinite(value);
	if (typeof value === "boolean") return value;
	if (value instanceof Date) return !Number.isNaN(value.getTime());
	if (typeof value === "object") return Object.keys(value).length > 0;

	return true;
}

export function FormSelect<TFieldValues extends FieldValues, TValue extends string | number = string>({
	control,
	name,
	rules,
	description,
	leftIcon,
	rightIcon,
	onRightIconClick,
	rightIconAriaLabel = "Action",
	parse,
	format,
	options,
	allowEmpty = false,
	emptyLabel = "Selecione...",
	emptyValue = "" as any,
	slotProps,
	fullWidth = true,
	onValueChange,
	required,
	label,
	labelIncludeRequiredText = false,
	multiple = false,
	placeholder,
	reserveHelperText = false,
	highlightActiveSelection = true,
	activeHighlightColor = DEFAULT_ACTIVE_SELECTION_COLOR,
	size = "small",
	...props
}: Readonly<FormSelectProps<TFieldValues, TValue>>) {
	return (
		<Controller
			control={control}
			name={name}
			rules={rules}
			render={({ field, fieldState }) => {
				const errorMsg = fieldState.error?.message;
				const helperTextValue = errorMsg ?? description ?? (reserveHelperText ? " " : undefined);

				const startAdornment = leftIcon ? (
					<InputAdornment position="start">{leftIcon}</InputAdornment>
				) : undefined;

				const endAdornment = rightIcon ? (
					<div>
						{onRightIconClick ? (
							<IconButton
								edge="end"
								onClick={onRightIconClick}
								aria-label={rightIconAriaLabel}
								tabIndex={-1}
								size="small"
							>
								{rightIcon}
							</IconButton>
						) : (
							rightIcon
						)}
					</div>
				) : undefined;

				const shownValue = multiple ? (field.value ?? []) : (field.value ?? emptyValue);

				const displayedValue = (() => {
					if (multiple) {
						const arr = shownValue as any[];

						if (format) return arr.map(format).join(", ");

						return arr as any;
					}

					return format ? format(shownValue as TValue) : shownValue;
				})();

				const hasActiveValue = possuiValorAtivo(field.value, emptyValue);

				const activeSelectionSx =
					highlightActiveSelection && hasActiveValue && !errorMsg
						? criarSxSelecaoAtiva(activeHighlightColor)
						: undefined;

				type EntradaSx = Exclude<NonNullable<SxProps<Theme>>, readonly unknown[]>;

				const partesSx: Array<EntradaSx | boolean> = [FORM_FIELD_BASE_SX];

				if (size === "extraSmall") partesSx.push(FORM_FIELD_EXTRA_SMALL_SX, FORM_SELECT_EXTRA_SMALL_SX);

				const adicionarSx = (sx?: SxProps<Theme>) => {
					if (!sx) return;

					if (Array.isArray(sx)) {
						partesSx.push(...(sx as Array<EntradaSx | boolean>));
						return;
					}

					partesSx.push(sx as EntradaSx);
				};

				adicionarSx(props.sx);
				adicionarSx(activeSelectionSx);

				const sxComposto: SxProps<Theme> = partesSx;
				const sizeMUI = getMuiFormFieldSize(size);

				const selectSlotProps = slotProps?.select as Record<string, any> | undefined;
				const rotuloValorVazio = allowEmpty ? emptyLabel : placeholder;
				const deveExibirValorVazio = allowEmpty || placeholder != null;
				const deveRenderizarValorVazio = Boolean(deveExibirValorVazio && !selectSlotProps?.renderValue);

				const renderizarValorVazio = () => (
					<Box component="span" sx={{ color: "text.secondary" }}>
						{rotuloValorVazio}
					</Box>
				);

				const renderizarLabelOpcao = (value: unknown) => {
					if (allowEmpty && isSameEmptyValue(value, emptyValue)) return emptyLabel;

					if (typeof value !== "string" && typeof value !== "number") return "";

					const option = options.find((opt) =>
						typeof opt.value === typeof value ? opt.value === value : String(opt.value) === String(value),
					);

					if (option?.label != null) return option.label;

					return String(value);
				};

				const renderizarValorSelect = (selected: unknown) => {
					if (multiple) {
						const values = Array.isArray(selected) ? selected : [];

						if (!values.length) return renderizarValorVazio();

						return values.flatMap((value, index) => [index > 0 ? ", " : null, renderizarLabelOpcao(value)]);
					}

					if (selected == null || isSameEmptyValue(selected, emptyValue)) return renderizarValorVazio();

					return renderizarLabelOpcao(selected);
				};

				const limparSelecionados = () => {
					field.onChange([]);
					(onValueChange as ((v: TValue[]) => void) | undefined)?.([]);
				};

				const possuiSelecionados = Array.isArray(displayedValue) && displayedValue.length > 0;

				return (
					<TextField
						{...props}
						select
						size={sizeMUI}
						label={withRequiredAsterisk(label, required, labelIncludeRequiredText)}
						name={field.name}
						value={displayedValue}
						onChange={(e) => {
							const raw = e.target.value;
							let next: any;

							if (multiple) {
								const arr = raw as unknown as string[];
								next = parse ? arr.map(parse) : arr;
							} else next = parse ? parse(raw) : raw;

							field.onChange(next);

							if (multiple) (onValueChange as ((v: TValue[]) => void) | undefined)?.(next);
							else (onValueChange as ((v: TValue) => void) | undefined)?.(next);
						}}
						onBlur={(event) => {
							field.onBlur();
							props.onBlur?.(event);
						}}
						inputRef={field.ref}
						error={!!errorMsg}
						helperText={helperTextValue}
						fullWidth={fullWidth}
						slotProps={{
							...slotProps,
							input: {
								...slotProps?.input,
								startAdornment,
								endAdornment,
							},
							htmlInput: {
								...slotProps?.htmlInput,
								required: !!required,
							},
							inputLabel: {
								...(deveExibirValorVazio ? { shrink: true } : {}),
								...slotProps?.inputLabel,
							},
							select: {
								...selectSlotProps,
								multiple,
								...(deveExibirValorVazio ? { displayEmpty: true } : {}),
								...(deveRenderizarValorVazio
									? {
											renderValue: renderizarValorSelect,
										}
									: {}),
							},
						}}
						sx={sxComposto}
					>
						{allowEmpty && <MenuItem value={emptyValue}>{emptyLabel}</MenuItem>}

						{multiple && (
							<ListSubheader
								disableGutters
								sx={{
									position: "sticky",
									top: 0,
									zIndex: 1,
									bgcolor: "background.paper",
									borderBottom: "1px solid",
									borderColor: "divider",
									px: 1,
									py: 0.75,
									lineHeight: "normal",
								}}
							>
								<Button
									type="button"
									size="small"
									variant="outlined"
									fullWidth
									color="error"
									disabled={!possuiSelecionados}
									onMouseDown={(event) => event.preventDefault()}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										limparSelecionados();
									}}
								>
									Limpar selecionados
								</Button>
							</ListSubheader>
						)}

						{options.map((opt) => (
							<MenuItem key={String(opt.value)} value={opt.value as any} disabled={opt.disabled}>
								<span className="truncate text-sm">{opt.label}</span>
							</MenuItem>
						))}
					</TextField>
				);
			}}
		/>
	);
}
